<template><div><component-combinator component-name="DtStack" /><h2 id="direction" tabindex="-1" class="d-docsite--header-2">Direction <a class="header-anchor d-link d-docsite--link d-link" href="#direction" aria-hidden="true">#</a></h2>
<h3 id="column" tabindex="-1" class="d-docsite--header-3">Column <a class="header-anchor d-link d-docsite--link d-link" href="#column" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>direction=&quot;column&quot;</code> will flow child items vertically, i.e. top to bottom. It is the default direction and doesn't need to be explictily set.</p>
<code-example source-code='&lt;dt-stack gap=&quot;200&quot;&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack gap="200">
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 1
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 2
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 3
    </dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h3 id="row" tabindex="-1" class="d-docsite--header-3">Row <a class="header-anchor d-link d-docsite--link d-link" href="#row" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>direction=&quot;row&quot;</code> will flow child items horizontally, i.e. left to right.</p>
<code-example source-code='&lt;dt-stack
  gap=&quot;200&quot;
  direction=&quot;row&quot;
&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
    gap="200"
    direction="row"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 1
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 2
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 3
    </dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h3 id="row-reverse" tabindex="-1" class="d-docsite--header-3">Row Reverse <a class="header-anchor d-link d-docsite--link d-link" href="#row-reverse" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack
  gap=&quot;200&quot;
  direction=&quot;row-reverse&quot;
&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
    gap="200"
    direction="row-reverse"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 1
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 2
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 3
    </dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h3 id="column-reverse" tabindex="-1" class="d-docsite--header-3">Column Reverse <a class="header-anchor d-link d-docsite--link d-link" href="#column-reverse" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack
  gap=&quot;200&quot;
  direction=&quot;column-reverse&quot;
&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
    gap="200"
    direction="column-reverse"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 1
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 2
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 3
    </dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h2 id="render-as" tabindex="-1" class="d-docsite--header-2">Render as <a class="header-anchor d-link d-docsite--link d-link" href="#render-as" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>as</code> prop controls which HTML element the Stack component renders as. Defaults to <code v-pre>&lt;div&gt;</code>, but can be declared as any valid HTML element to ensure semantic and accessible markup.</p>
<p class="d-docsite--paragraph">Declaring as an appropriate HTML element improves accessibility by helping screen readers better understand document structure, and maintainability with clear code intent.</p>
<p class="d-docsite--paragraph"><strong>Common <code v-pre>as</code> values:</strong> <code v-pre>span</code>, <code v-pre>ul</code>, <code v-pre>ol</code>, <code v-pre>li</code>, <code v-pre>section</code>, <code v-pre>nav</code>, <code v-pre>article</code>, <code v-pre>main</code>, <code v-pre>aside</code>, <code v-pre>header</code>, <code v-pre>footer</code>.</p>
<h3 id="example-section" tabindex="-1" class="d-docsite--header-3">Example: section <a class="header-anchor d-link d-docsite--link d-link" href="#example-section" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>as=&quot;section&quot;</code> to create a thematic grouping of content.</p>
<code-example source-code='&lt;dt-stack
  as=&quot;section&quot;
  gap=&quot;100&quot;
&gt;
  &lt;dt-box&gt;Stack item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Stack item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Stack item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
    as="section"
    gap="100"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 3</dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h3 id="example-span" tabindex="-1" class="d-docsite--header-3">Example: span <a class="header-anchor d-link d-docsite--link d-link" href="#example-span" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>as=&quot;span&quot;</code> when you need an inline container.</p>
<code-example source-code='&lt;dt-stack
  as=&quot;span&quot;
  direction=&quot;row&quot;
  gap=&quot;100&quot;
&gt;
  &lt;dt-box&gt;Inline item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Inline item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Inline item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
    as="span"
    direction="row"
    gap="100"
  >
    <dt-box as="span" surface="moderate-opaque" padding="200" border-radius="300">Inline item 1</dt-box>
    <dt-box as="span" surface="moderate-opaque" padding="200" border-radius="300">Inline item 2<br>with a second line</dt-box>
    <dt-box as="span" surface="moderate-opaque" padding="200" border-radius="300">Inline item 3</dt-box>
  </dt-stack>
</dt-box>
</code-example>
<h2 id="gap" tabindex="-1" class="d-docsite--header-2">Gap <a class="header-anchor d-link d-docsite--link d-link" href="#gap" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-stack gap=&quot;100&quot;&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="200" class="d-w100p">
  <div class="d-d-none xl:d-d-flex d-jc-center">
    <dt-segmented-control
      :size="100"
      :model-value="selectedGap"
      aria-label="Gap size"
      @update:model-value="setGap"
    >
      <dt-segmented-control-item
        v-for="gap in gaps"
        v-dt-tooltip="{ message: gapToPx(gap), delay: false }"
        :key="gap"
        :value="gap"
        :selected="gap === selectedGap"
      >
        {{ gap }}
      </dt-segmented-control-item>
    </dt-segmented-control>
  </div>
  <dt-stack
    :direction="{ 'default': 'column', 'md': 'row' }"
    gap="200"
    class="d-w100p"
    align="start"
  >
    <dt-stack class="d-w100p md:d-w50p" gap="50">
      <dt-text as="h3" kind="headline" :size="300">Column</dt-text>
      <dt-stack
        :gap="selectedGap"
        class="d-bgc-moderate-opaque d-t d-td300 d-bar-400 d-ttf-quint"
      >
        <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 1</dt-box>
        <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 2</dt-box>
        <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 3</dt-box>
      </dt-stack>
    </dt-stack>
    <dt-stack class="d-w100p md:d-w50p" gap="50">
      <dt-text as="h3" kind="headline" :size="300">Row</dt-text>
      <dt-stack
        direction="row"
        :gap="selectedGap"
        class="d-bgc-moderate-opaque d-t d-td300 d-bar-400 d-ttf-quint"
      >
        <dt-box surface="moderate-opaque" padding="200" border-radius="300" class="d-fl1">Stack item 1</dt-box>
        <dt-box surface="moderate-opaque" padding="200" border-radius="300" class="d-fl1">Stack item 2</dt-box>
        <dt-box surface="moderate-opaque" padding="200" border-radius="300" class="d-fl1">Stack item 3</dt-box>
      </dt-stack>
    </dt-stack>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="available-gaps" tabindex="-1" class="d-docsite--header-3">Available gaps <a class="header-anchor d-link d-docsite--link d-link" href="#available-gaps" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <dt-box>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0">
            <div class="d-p-200 d-bb d-bbw1">Size</div>
          </th>
          <th scope="col" class="d-p-0 d-bbw0">
            <div class="d-p-200 d-bb d-bbw1">Design Token</div>
          </th>
          <th scope="col" class="d-ta-right d-p-0 d-bbw0">
            <div class="d-p-200 d-bb d-bbw1">REM</div>
          </th>
          <th scope="col" class="d-ta-right d-p-0 d-bbw0">
            <div class="d-p-200 d-bb d-bbw1">PX</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ value: stop, output } in spacingValues" :key="stop" valign="baseline">
          <th scope="row" class="d-code--sm d-docsite-code">{{ stop }}</th>
          <td><dt-text kind="code" size="xs">var(--dt-spacing-{{ stop }})</dt-text></td>
          <td class="d-code--sm d-docsite-code d-ta-right">{{ output }}</td>
          <td class="d-code--sm d-docsite-code d-ta-right">{{ (parseFloat(output) * 10) }}px</td>
        </tr>
      </tbody>
    </table>
  </dt-box>
</clamped-table-wrapper>
<h2 id="align" tabindex="-1" class="d-docsite--header-2">Align <a class="header-anchor d-link d-docsite--link d-link" href="#align" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>align</code> prop controls how items are aligned along the cross-axis (perpendicular to the stack direction). For row stacks, this controls vertical alignment. For column stacks, this controls horizontal alignment.</p>
<p class="d-docsite--paragraph">Available <code v-pre>align</code> values: <code v-pre>start</code>, <code v-pre>center</code>, <code v-pre>end</code>, <code v-pre>stretch</code>, <code v-pre>baseline</code>.</p>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The <code v-pre>align</code> prop is optional. Unless specified, it will default vertical stacks to <code v-pre>align-items=&quot;stretch&quot;</code> and horizontal stacks to <code v-pre>align-items=&quot;center&quot;</code>.</p>
<code-example source-code='&lt;dt-stack gap=&quot;100&quot;&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot;&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-stretch"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
  <dt-stack
    gap="100"
    direction="row"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="start" tabindex="-1" class="d-docsite--header-3">Start <a class="header-anchor d-link d-docsite--link d-link" href="#start" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the start of the cross-axis.</p>
<code-example source-code='&lt;dt-stack
  gap=&quot;100&quot;
  align=&quot;start&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;
&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  align=&quot;start&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="100"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    align="start"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-start"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    align="start"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-start"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="center" tabindex="-1" class="d-docsite--header-3">Center <a class="header-anchor d-link d-docsite--link d-link" href="#center" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Center items along the cross-axis.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  align=&quot;center&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    align="center"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    align="center"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="end" tabindex="-1" class="d-docsite--header-3">End <a class="header-anchor d-link d-docsite--link d-link" href="#end" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the end of the cross-axis.</p>
<code-example source-code='&lt;dt-stack
  gap=&quot;100&quot;
  align=&quot;end&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;
&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  align=&quot;end&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    align="end"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-end"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    align="end"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-end"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="stretch" tabindex="-1" class="d-docsite--header-3">Stretch <a class="header-anchor d-link d-docsite--link d-link" href="#stretch" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stretch items to fill the container height.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  align=&quot;stretch&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller item&lt;br&gt;
    with more content
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    align="stretch"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-stretch"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    align="stretch"
    class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-stretch"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Taller item<br>
      with more content
    </dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Short
    </dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="baseline" tabindex="-1" class="d-docsite--header-3">Baseline <a class="header-anchor d-link d-docsite--link d-link" href="#baseline" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items along their text baselines.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  align=&quot;baseline&quot;
&gt;
  &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot;&gt;Small body&lt;/dt-text&gt;
  &lt;dt-text kind=&quot;body&quot; :size=&quot;300&quot;&gt;Medium body&lt;/dt-text&gt;
  &lt;dt-text kind=&quot;headline&quot; :size=&quot;600&quot;&gt;Large headline&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  direction="row"
  gap="100"
  align="baseline"
  class="d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--baseline"
>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    <dt-text kind="body" :size="100">Small body</dt-text>
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    <dt-text kind="body" :size="300">Medium body</dt-text>
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    <dt-text kind="headline" :size="600">Large headline</dt-text>
  </dt-box>
</dt-stack>
</code-example>
<h2 id="justify" tabindex="-1" class="d-docsite--header-2">Justify <a class="header-anchor d-link d-docsite--link d-link" href="#justify" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>justify</code> prop controls how items are distributed along the main axis (the direction of the stack). For row stacks, this controls horizontal distribution. For column stacks, this controls vertical distribution.</p>
<p class="d-docsite--paragraph">Available <code v-pre>justify</code> values: <code v-pre>start</code> (default), <code v-pre>center</code>, <code v-pre>end</code>, <code v-pre>space-around</code>, <code v-pre>space-between</code>, <code v-pre>space-evenly</code>.</p>
<h3 id="start-1" tabindex="-1" class="d-docsite--header-3">Start <a class="header-anchor d-link d-docsite--link d-link" href="#start-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the start of the main axis (default).</p>
<code-example source-code='&lt;dt-stack justify=&quot;start&quot;&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  align="stretch"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="start"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-start"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="start"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-start"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="center-1" tabindex="-1" class="d-docsite--header-3">Center <a class="header-anchor d-link d-docsite--link d-link" href="#center-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Center items along the main axis.</p>
<code-example source-code='&lt;dt-stack justify=&quot;center&quot;&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="center"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="center"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="end-1" tabindex="-1" class="d-docsite--header-3">End <a class="header-anchor d-link d-docsite--link d-link" href="#end-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the end of the main axis.</p>
<code-example source-code='&lt;dt-stack justify=&quot;end&quot;&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="end"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-end"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="end"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-end"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="space-around" tabindex="-1" class="d-docsite--header-3">Space Around <a class="header-anchor d-link d-docsite--link d-link" href="#space-around" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with equal space around each item.</p>
<code-example source-code='&lt;dt-stack justify=&quot;space-around&quot;&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="space-around"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="space-around"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="space-between" tabindex="-1" class="d-docsite--header-3">Space Between <a class="header-anchor d-link d-docsite--link d-link" href="#space-between" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with space between them, edges flush to container.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  justify=&quot;space-between&quot;
  class=&quot;d-w100p&quot;
&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="space-between"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="space-between"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="space-evenly" tabindex="-1" class="d-docsite--header-3">Space Evenly <a class="header-anchor d-link d-docsite--link d-link" href="#space-evenly" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with equal space between all items, including edges.</p>
<code-example source-code='&lt;dt-stack justify=&quot;space-evenly&quot;&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  class="d-w100p"
  gap="200"
  :direction="{ default: `column`, md: `row` }"
>
  <dt-stack
    gap="100"
    justify="space-evenly"
    class="d-w100p d-h-400 d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--block-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
  <dt-stack
    direction="row"
    gap="100"
    justify="space-evenly"
    class="d-w100p d-bgc-moderate-opaque d-bar-400 axis-outline axis-outline--inline-center"
  >
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="responsive" tabindex="-1" class="d-docsite--header-2">Responsive <a class="header-anchor d-link d-docsite--link d-link" href="#responsive" aria-hidden="true">#</a></h2>
<h3 id="example" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stacks column at small screen size and column reverse at large screen</p>
<code-example source-code='&lt;dt-stack
  :direction=&quot;{ default: `row`, sm: `column`, lg: `column-reverse` }&quot;
  gap=&quot;100&quot;
&gt;
  &lt;dt-box&gt;
    Stack item 1
  &lt;/dt-box&gt;
  &lt;dt-box&gt;
    Stack item 2
  &lt;/dt-box&gt;
  &lt;dt-box&gt;
    Stack item 3
  &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  :direction="{ default: `row`, sm: `column`, lg: `column-reverse` }"
  gap="100"
  class="d-bgc-moderate-opaque"
>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 1
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 2
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 3
  </dt-box>
</dt-stack>
</code-example>
<p class="d-docsite--paragraph">Set <code v-pre>200</code> as the default gap, <code v-pre>300</code> for small and larger, <code v-pre>400</code> for medium, <code v-pre>500</code> for large, and <code v-pre>600</code> for extra large. Learn more about how our breakpoints work in the <RouteLink to="/utilities/responsive/breakpoints.html" class="d-docsite--link d-link">Responsive Breakpoints documentation</RouteLink>.</p>
<code-example source-code='&lt;dt-stack :gap=&quot;{ default: `200`, xl: `600`, lg: `500`, md: `400`, sm: `300` }&quot;&gt;
  &lt;dt-box&gt; Stack item 1 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 2 &lt;/dt-box&gt;
  &lt;dt-box&gt; Stack item 3 &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  :gap="{ default: '200', xl: '600', lg: '500', md: '400', sm: '300' }"
  class="d-bgc-moderate-opaque"
>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 1
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 2
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Stack item 3
  </dt-box>
</dt-stack>
</code-example>
<h3 id="nested-example" tabindex="-1" class="d-docsite--header-3">Nested Example <a class="header-anchor d-link d-docsite--link d-link" href="#nested-example" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stacks row with gap 500 and stacks in row reverse the nested stack with gap 500.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  as=&quot;section&quot;
  gap=&quot;200&quot;
&gt;
  &lt;dt-stack&gt;
    Stack item 1
  &lt;/dt-stack&gt;
  &lt;dt-stack gap=&quot;200&quot;&gt;
    &lt;dt-box&gt;Stack item 2&lt;/dt-box&gt;
    &lt;dt-stack
      direction=&quot;row-reverse&quot;
      gap=&quot;200&quot;
    &gt;
      &lt;dt-box&gt;Stack item 3&lt;br&gt;with multiple lines&lt;/dt-box&gt;
      &lt;dt-box&gt;Stack item 4&lt;/dt-box&gt;
    &lt;/dt-stack&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack direction="row" as="section" gap="200">
    <dt-box surface="moderate-opaque" padding="200" border-radius="300">
      Stack item 1
    </dt-box>
    <dt-stack gap="200">
      <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 2</dt-box>
      <dt-stack direction="row-reverse" gap="200">
        <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 3<br>with multiple lines</dt-box>
        <dt-box surface="moderate-opaque" padding="200" border-radius="300">Stack item 4</dt-box>
      </dt-stack>
    </dt-stack>
  </dt-stack>
</dt-box>
</code-example>
<h3 id="example-align-and-justify" tabindex="-1" class="d-docsite--header-3">Example: Align and Justify <a class="header-anchor d-link d-docsite--link d-link" href="#example-align-and-justify" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Like <code v-pre>direction</code> and <code v-pre>gap</code>, the <code v-pre>align</code> and <code v-pre>justify</code> props support responsive object syntax to change alignment at different breakpoints.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  gap=&quot;100&quot;
  :align=&quot;{ default: `start`, md: `center`, lg: `end` }&quot;
&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
  &lt;dt-box&gt;
    Taller&lt;br&gt;
    item
  &lt;/dt-box&gt;
  &lt;dt-box&gt;Short&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-box surface="moderate-opaque" border-radius="300">
  <dt-stack
  direction="row"
  gap="100"
  :align="{ default: 'start', md: 'center', lg: 'end' }"
  >
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Short
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Taller<br>
    item
  </dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">
    Short
  </dt-box>
</dt-stack>
</dt-box>
</code-example>
<p class="d-docsite--paragraph">Resize your browser to see the alignment change at different breakpoints.</p>
<code-example source-code='&lt;dt-stack
  direction=&quot;row&quot;
  :justify=&quot;{ default: `start`, md: `center`, lg: `space-between` }&quot;
  class=&quot;d-w100p&quot;
&gt;
  &lt;dt-box&gt;Item 1&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 2&lt;/dt-box&gt;
  &lt;dt-box&gt;Item 3&lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack
  direction="row"
  gap="0"
  :justify="{ default: 'start', md: 'center', lg: 'space-between' }"
  class="d-w100p d-bgc-moderate-opaque d-bar-400"
>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 1</dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 2</dt-box>
  <dt-box surface="moderate-opaque" padding="200" border-radius="300">Item 3</dt-box>
</dt-stack>
</code-example>
<p class="d-docsite--paragraph">Resize your browser to see the justification change at different breakpoints.</p>
<h2 id="migrating-from-flex-css-utilities" tabindex="-1" class="d-docsite--header-2">Migrating from Flex CSS Utilities <a class="header-anchor d-link d-docsite--link d-link" href="#migrating-from-flex-css-utilities" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">View the <a href="/about/whats-new/posts/2025-12-2" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Migrating from Flex CSS Utilities to DtStack</a> for more details.</p>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="profile-card" tabindex="-1" class="d-docsite--header-3">Profile Card <a class="header-anchor d-link d-docsite--link d-link" href="#profile-card" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack gap=&quot;200&quot;&gt;
  &lt;dt-stack gap=&quot;100&quot; justify=&quot;space-between&quot;&gt;
    &lt;dt-stack&gt;
      &lt;dt-text as=&quot;h2&quot; kind=&quot;headline&quot; :size=&quot;500&quot; strength=&quot;medium&quot; density=&quot;200&quot; class=&quot;d-fs-400&quot;&gt;
        Katie Rodriguez
      &lt;/dt-text&gt;
      &lt;dt-stack direction=&quot;row&quot; gap=&quot;350&quot;&gt;
        &lt;dt-text tone=&quot;positive&quot;&gt;
          Available
        &lt;/dt-text&gt;
        &lt;dt-text&gt;&amp;bull;&lt;/dt-text&gt;
        &lt;dt-text tone=&quot;tertiary&quot;&gt;
          Working from coffee shop
        &lt;/dt-text&gt;
      &lt;/dt-stack&gt;
    &lt;/dt-stack&gt;
    &lt;dt-stack gap=&quot;200&quot;&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;300&quot; density=&quot;200&quot; strength=&quot;semibold&quot; tone=&quot;tertiary&quot;&gt;
        Chief Customer Success Officer
      &lt;/dt-text&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; density=&quot;200&quot;&gt;
        &lt;dt-text strength=&quot;semibold&quot;&gt;
          6:19 am
        &lt;/dt-text&gt; local time
      &lt;/dt-text&gt;
    &lt;/dt-stack&gt;
  &lt;/dt-stack&gt;
  &lt;dt-stack gap=&quot;100&quot; direction=&quot;row&quot; justify=&quot;space-between&quot;&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-phone :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Call
    &lt;/dt-button&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-quick-reply :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Message
    &lt;/dt-button&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-video :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Meet
    &lt;/dt-button&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<ExampleProfileCard />
</code-example>
<h3 id="call-log" tabindex="-1" class="d-docsite--header-3">Call Log <a class="header-anchor d-link d-docsite--link d-link" href="#call-log" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack gap=&quot;100&quot; class=&quot;d-w-800&quot;&gt;
  &lt;dt-text as=&quot;h2&quot; kind=&quot;headline&quot; :size=&quot;400&quot;&gt;Saturday, May 24, 2025&lt;/dt-text&gt;
  &lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-w100p&quot;&gt;
    &lt;dt-avatar full-name=&quot;Ashanti Trevor&quot; /&gt;
    &lt;dt-stack class=&quot;d-fl1&quot;&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; strength=&quot;bold&quot;&gt;Ashanti Trevor&lt;/dt-text&gt;
      &lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot;&gt;
        &lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
          &lt;dt-icon name=&quot;phone-outgoing&quot; size=&quot;200&quot; class=&quot;d-fc-tertiary&quot; /&gt;
          &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;Outgoing call&lt;/dt-text&gt;
        &lt;/dt-stack&gt;
        &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;&amp;bull;&lt;/dt-text&gt;
        &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;2 minutes 10 seconds&lt;/dt-text&gt;
      &lt;/dt-stack&gt;
    &lt;/dt-stack&gt;
    &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; tone=&quot;tertiary&quot; numeric&gt;3:23 pm&lt;/dt-text&gt;
    &lt;dt-badge kind=&quot;count&quot; type=&quot;bulletin&quot; text=&quot;6&quot; /&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="100" class="d-w-800">
  <dt-text as="h2" kind="headline" :size="400">Saturday, May 24, 2025</dt-text>
  <dt-stack direction="row" gap="100" class="d-w100p">
    <dt-avatar full-name="Ashanti Trevor" />
    <dt-stack class="d-fl1">
      <dt-text kind="body" :size="200" strength="bold">Ashanti Trevor</dt-text>
      <dt-stack direction="row" gap="50">
        <dt-stack direction="row" gap="100">
          <dt-icon name="phone-outgoing" size="200" class="d-fc-tertiary" />
          <dt-text kind="body" :size="100" tone="tertiary">Outgoing call</dt-text>
        </dt-stack>
        <dt-text kind="body" :size="100" tone="tertiary">&bull;</dt-text>
        <dt-text kind="body" :size="100" tone="tertiary">2 minutes 10 seconds</dt-text>
      </dt-stack>
    </dt-stack>
    <dt-text kind="body" :size="200" tone="tertiary" numeric>3:23 pm</dt-text>
    <dt-badge kind="count" type="bulletin" text="6" />
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="stack" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="stack"></component-class-table></div></template>


<script setup>
  import { ref } from 'vue';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
  import ExampleProfileCard from '@exampleComponents/ExampleProfileCard.vue';
  import { values as spacingValues } from '@data/spacing.json';

  const selectedGap = ref('100');

  const gapToPx = (gap) => {
    const entry = spacingValues.find(v => v.value === gap);
    return entry ? `${parseFloat(entry.output) * 10}px` : gap;
  };

  const setGap = (gap) => {
    selectedGap.value = gap;
  };

  const allGaps = window.DIALTONE_CONSTANTS.DT_STACK_GAP;
  const gaps = allGaps.slice(0, allGaps.indexOf('400') + 1);
</script>
<style scoped lang="less">
  .axis-outline {
    --axis-size: var(--dt-spacing-25);
    --axis-offset: var(--dt-spacing-25-negative);
    --axis-color: var(--dt-color-border-critical);
    --axis-opacity: var(--dt-opacity-800);
    --axis-pattern-vertical: repeating-linear-gradient(
      to bottom,
      var(--axis-color) 0,
      var(--axis-color) calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 4)
    );
    --axis-pattern-horizontal: repeating-linear-gradient(
      to right,
      var(--axis-color) 0,
      var(--axis-color) calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 4)
    );

    position: relative;

    &:before,
    &:after {
      content: "";
      position: absolute;
      z-index: 1;
      border-radius: var(--dt-size-radius-pill);
      background-image: var(--axis-pattern-vertical); // Default to vertical
      opacity: var(--axis-opacity);
    }

    &:hover:before,
    &:hover:after {
      --axis-opacity: var(--dt-opacity-1300);
      background-image: none;
      background-color: var(--axis-color);
    }

    // Inline axis indicators (vertical lines)
    &--inline-start::before {
      inset-inline-start: var(--axis-offset);
      inline-size: var(--axis-size);
      block-size: 100%;
    }

    &--inline-center::before {
      inset-inline-start: 50%;
      inset-block-start: 50%;
      transform: translate(-50%, -50%);
      inline-size: var(--axis-size);
      block-size: 100%;
    }

    &--inline-end::before {
      inset-inline-end: var(--axis-offset);
      inline-size: var(--axis-size);
      block-size: 100%;
    }

    &--inline-stretch {
      &::before {
        inset-inline-start: var(--axis-offset);
        inline-size: var(--axis-size);
        block-size: 100%;
      }

      &::after {
        inset-inline-end: var(--axis-offset);
        inline-size: var(--axis-size);
        block-size: 100%;
      }
    }

    // Block axis indicators (horizontal lines)
    &--block-start::before {
      inset-block-start: var(--axis-offset);
      block-size: var(--axis-size);
      inline-size: 100%;
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-center::before {
      inset-inline-start: 50%;
      inset-block-start: 50%;
      transform: translate(-50%, -50%);
      inline-size: 100%;
      block-size: var(--axis-size);
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-end::before {
      inset-block-end: var(--axis-offset);
      block-size: var(--axis-size);
      inline-size: 100%;
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-stretch {
      &::before {
        inset-block-start: var(--axis-offset);
        block-size: var(--axis-size);
        inline-size: 100%;
        background-image: var(--axis-pattern-horizontal);
      }

      &::after {
        inset-block-end: var(--axis-offset);
        block-size: var(--axis-size);
        inline-size: 100%;
        background-image: var(--axis-pattern-horizontal);
      }
    }

    // Special case for baseline
    &--baseline::before {
      inset-block-end: var(--dt-spacing-300);
      block-size: var(--axis-size);
      inline-size: 100%;
      background-image: var(--axis-pattern-horizontal);
    }
  }
</style>